import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface StreamingVisualState {
  isGenerating: boolean;
  content: string;
  svg: string | null;
  dataUrl: string | null;
  isComplete: boolean;
  error: string | null;
}

export const useStreamingVisualCreator = () => {
  const [streamingState, setStreamingState] = useState<StreamingVisualState>({
    isGenerating: false,
    content: '',
    svg: null,
    dataUrl: null,
    isComplete: false,
    error: null,
  });

  const streamGenerateVisual = useCallback(async (
    prompt: string,
    visualType: string = "auto",
    aspectRatio: string = "4:3",
    options?: { mode?: 'describe' | 'convert'; originalSvg?: string | null; targetTypes?: string[] | null }
  ) => {
    setStreamingState({
      isGenerating: true,
      content: '',
      svg: null,
      dataUrl: null,
      isComplete: false,
      error: null,
    });

    try {
      // The edge function returns a streaming response
      const response = await fetch(`https://zctacexxhzjuntlnltqa.supabase.co/functions/v1/generate-visual-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          prompt,
          visualType,
          aspectRatio,
          mode: options?.mode ?? 'describe',
          originalSvg: options?.originalSvg ?? null,
          targetTypes: options?.targetTypes ?? null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          while (true) {
            const lineEnd = buffer.indexOf('\n\n');
            if (lineEnd === -1) break;

            const chunk = buffer.slice(0, lineEnd).trim();
            buffer = buffer.slice(lineEnd + 2);

            if (chunk.startsWith('data: ')) {
              const data = chunk.slice(6);
              try {
                const parsed = JSON.parse(data);
                
                if (parsed.type === 'visual_delta') {
                  setStreamingState(prev => ({
                    ...prev,
                    content: prev.content + parsed.content,
                  }));
                } else if (parsed.type === 'visual_progress' && parsed.svg_preview) {
                  setStreamingState(prev => ({
                    ...prev,
                    content: prev.content + parsed.content,
                    svg: parsed.svg_preview,
                    dataUrl: `data:image/svg+xml;utf8,${encodeURIComponent(parsed.svg_preview)}`,
                  }));
                } else if (parsed.type === 'visual_complete') {
                  setStreamingState(prev => ({
                    ...prev,
                    svg: parsed.svg,
                    dataUrl: parsed.dataUrl,
                    isComplete: true,
                    isGenerating: false,
                  }));
                  
                  // Dispatch credits update event
                  if (parsed.credits_remaining !== undefined) {
                    window.dispatchEvent(new CustomEvent('creditsUpdated', {
                      detail: { credits_remaining: parsed.credits_remaining }
                    }));
                  }
                  
                  return {
                    svg: parsed.svg,
                    dataUrl: parsed.dataUrl,
                    credits_remaining: parsed.credits_remaining
                  };
                } else if (parsed.type === 'error') {
                  throw new Error(parsed.error);
                }
              } catch (e) {
                console.error('Error parsing stream data:', e);
              }
            }
          }
        }
      } finally {
        reader.cancel();
      }
    } catch (error) {
      console.error('Visual streaming error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to stream visual';
      
      setStreamingState(prev => ({
        ...prev,
        error: errorMessage,
        isGenerating: false,
      }));

      if (errorMessage.includes('No active subscription') || errorMessage.includes('No credits remaining')) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      
      throw error;
    }
  }, []);

  const reset = useCallback(() => {
    setStreamingState({
      isGenerating: false,
      content: '',
      svg: null,
      dataUrl: null,
      isComplete: false,
      error: null,
    });
  }, []);

  return {
    streamingState,
    streamGenerateVisual,
    reset,
  };
};